import { useMemo } from 'react';
import { ModuleCategory, ModuleSubCategory } from '@/apolloClient.types';
import type { FetchAvailableModulesQuery } from '@/pages/CompanyManagementPage/shared/Companies.api';
import {
    useFetchAvailableModulesQuery,
    useFetchCompanyDetailsQuery,
} from '@/pages/CompanyManagementPage/shared/Companies.api';

export type EnabledModuleDefinition = FetchAvailableModulesQuery['availableModules']['items'][number];

export interface ModuleFilter {
    category?: ModuleCategory;
    subCategory?: ModuleSubCategory;
}

export interface CompanyModuleAccessHook {
    loading: boolean;
    companyId: string | null;
    has: (identifier: string) => boolean;
    /** Is at least one enabled module matching the given category/subCategory filter? */
    hasAny: (filter: ModuleFilter) => boolean;
    /** Returns the enabled module definitions */
    getEnabledDefinitions: () => EnabledModuleDefinition[];

    // --- Category-level convenience (stable count — one per ModuleCategory) ---
    hasAssessments: () => boolean;
    hasTraining: () => boolean;
    hasNutrition: () => boolean;

    // --- Assessment subcategory convenience (stable count — drives Results menu tabs) ---
    hasCognitiveAssessments: () => boolean;
    hasPsychologicalAssessments: () => boolean;
    hasPhysiologicalAssessments: () => boolean;

    // --- External integrations ---
    hasPerformance: () => boolean;
    hasAnyExternalIntegration: () => boolean;

    // --- Composite business rules (encode report ↔ assessment dependencies) ---
    canAccessIndividualReport: () => boolean;
    canAccessTeamResilienceReport: () => boolean;
    canAccessPersonalCopingReport: () => boolean;
    hasAnyReport: () => boolean;
}

/**
 * useCompanyModuleAccess
 *
 * Exposes a predicate API derived from the company's enabled module identifiers,
 * cross-referenced with the full set of available module definitions.
 *
 * Data sources:
 * - Company.enabledModules (list of identifiers)
 * - availableModules (category/subCategory metadata per identifier)
 *
 * - Adding a new module identifier does NOT require editing this hook — callers pass the
 *   identifier to `has()` or use `hasAny({ category, subCategory })`.
 * - The category / subcategory convenience helpers are stable in count (one per
 *   ModuleCategory / ModuleSubCategory) and exist to deduplicate repeated call sites.
 * - Report ↔ assessment dependencies are encoded in the composite helpers below. Keep these
 *   in sync with the backend validation in updateCompany/createCompany.
 */
const useCompanyModuleAccess = (companyId: string | null | undefined): CompanyModuleAccessHook => {
    const { data: companyData, loading: companyLoading } = useFetchCompanyDetailsQuery({
        variables: { id: companyId! },
        fetchPolicy: 'cache-and-network',
        skip: !companyId,
    });

    const enabledIdentifiers: string[] = useMemo(() => companyData?.company?.enabledModules ?? [], [companyData]);

    // Fetch module definitions
    const { data: availableData, loading: availableLoading } = useFetchAvailableModulesQuery({
        variables: { filter: { identifiers: enabledIdentifiers } },
        fetchPolicy: 'cache-and-network',
        skip: !companyId,
    });

    return useMemo<CompanyModuleAccessHook>(() => {
        const enabledDefinitions = availableData?.availableModules?.items ?? [];
        const enabledSet = new Set(enabledDefinitions.map(def => def.identifier));

        const has = (identifier: string): boolean => enabledSet.has(identifier);

        const hasAny = ({ category, subCategory }: ModuleFilter): boolean =>
            enabledDefinitions.some(def => {
                if (category && def.category !== category) {
                    return false;
                }
                if (subCategory !== undefined && subCategory !== null) {
                    const defSubCategory = 'subCategory' in def ? def.subCategory : null;
                    if (defSubCategory !== subCategory) {
                        return false;
                    }
                }
                return true;
            });

        // Category-level
        const hasAssessments = (): boolean => hasAny({ category: ModuleCategory.Assessment });
        const hasTraining = (): boolean => hasAny({ category: ModuleCategory.Training });
        const hasNutrition = (): boolean => hasAny({ category: ModuleCategory.Nutrition });

        // Assessment subcategory
        const hasCognitiveAssessments = (): boolean =>
            hasAny({ category: ModuleCategory.Assessment, subCategory: ModuleSubCategory.Cognitive });
        const hasPsychologicalAssessments = (): boolean =>
            hasAny({ category: ModuleCategory.Assessment, subCategory: ModuleSubCategory.Psychological });
        const hasPhysiologicalAssessments = (): boolean =>
            hasAny({ category: ModuleCategory.Assessment, subCategory: ModuleSubCategory.Physical });

        // External
        const hasPerformance = (): boolean =>
            hasAny({ category: ModuleCategory.External, subCategory: ModuleSubCategory.Performance });
        const hasAnyExternalIntegration = (): boolean => has('vald') || has('polar-watch');

        // Composites — report ↔ assessment dependencies.
        // Keep these identifiers in sync with the backend validator in mutations/company/utils.ts.
        const canAccessTeamResilienceReport = (): boolean => has('team-resilience-report') && has('team-resilience');
        const canAccessPersonalCopingReport = (): boolean =>
            has('personal-coping-report') && has('personal-coping-style');

        const hasQualifyingAssessmentForIndividualReport = (): boolean =>
            enabledDefinitions.some(
                def => def.category === ModuleCategory.Assessment && def.identifier !== 'personal-coping-style'
            );
        const canAccessIndividualReport = (): boolean =>
            has('individual-report') && hasQualifyingAssessmentForIndividualReport();

        const hasAnyReport = (): boolean =>
            canAccessIndividualReport() || canAccessTeamResilienceReport() || canAccessPersonalCopingReport();

        return {
            loading: !!companyId && (companyLoading || availableLoading),
            companyId: companyId || null,
            has,
            hasAny,
            getEnabledDefinitions: () => enabledDefinitions,
            hasAssessments,
            hasTraining,
            hasNutrition,
            hasCognitiveAssessments,
            hasPsychologicalAssessments,
            hasPhysiologicalAssessments,
            hasPerformance,
            hasAnyExternalIntegration,
            canAccessIndividualReport,
            canAccessTeamResilienceReport,
            canAccessPersonalCopingReport,
            hasAnyReport,
        };
    }, [companyId, companyLoading, availableLoading, availableData]);
};

export default useCompanyModuleAccess;
